import React, { useEffect, useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import { getIntakeFormFields, createIntakeFormField, updateIntakeFormField, deleteIntakeFormField, getServices } from '../../services/api';
import { safeParseJSON } from '../../context/AuthContext';
import { useConfirm } from '../../components/ConfirmDialog';

const FIELD_TYPES = [
  { value: 'text',     label: 'Texto curto' },
  { value: 'textarea', label: 'Texto longo' },
  { value: 'select',   label: 'Seleção (lista)' },
  { value: 'checkbox', label: 'Caixa de seleção' },
  { value: 'number',   label: 'Número' },
  { value: 'date',     label: 'Data' },
];

const emptyForm = { label: '', type: 'text', required: false, options: '', sort_order: 0, service_id: '' };

export default function TenantIntakeForms() {
  const confirm = useConfirm();
  const [fields,   setFields]   = useState([]);
  const [services, setServices] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [modal,    setModal]    = useState(null);
  const [form,     setForm]     = useState(emptyForm);
  const [saving,   setSaving]   = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([getIntakeFormFields(), getServices()])
      .then(([f, s]) => { setFields(f.data.fields || []); setServices(s.data.services || []); })
      .catch(() => toast.error('Erro ao carregar dados'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setForm(emptyForm); setModal('create'); };
  const openEdit   = f => {
    setForm({
      label: f.label, type: f.type, required: !!f.required,
      options: (safeParseJSON(f.options, []) || []).join('\n'),
      sort_order: f.sort_order || 0,
      service_id: f.service_id || '',
    });
    setModal(f);
  };

  const onSave = async e => {
    e.preventDefault();
    if (!form.label.trim()) return toast.error('Label é obrigatório');
    setSaving(true);
    try {
      const opts = form.type === 'select' && form.options
        ? form.options.split('\n').map(o => o.trim()).filter(Boolean)
        : null;
      const payload = { ...form, options: opts, service_id: form.service_id || null, required: form.required ? 1 : 0 };

      if (modal === 'create') { await createIntakeFormField(payload); toast.success('Campo criado'); }
      else { await updateIntakeFormField(modal.id, payload); toast.success('Campo atualizado'); }
      setModal(null);
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'Erro ao salvar'); }
    finally { setSaving(false); }
  };

  const onDelete = async id => {
    const ok = await confirm({
      title: 'Remover campo do formulário?',
      message: 'O campo será removido dos agendamentos futuros. Respostas já coletadas em agendamentos antigos são preservadas.',
      confirmText: 'Remover',
      variant: 'danger',
    });
    if (!ok) return;
    try { await deleteIntakeFormField(id); toast.success('Campo removido'); load(); }
    catch (err) { toast.error(err.response?.data?.error || 'Erro ao remover'); }
  };

  return (
    <div className="page-body">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: 0, color: 'var(--gray-900)', fontSize: 22, fontWeight: 700 }}>Formulário de Entrada</h1>
          <p style={{ margin: '4px 0 0', color: 'var(--gray-500)', fontSize: 13 }}>Perguntas exibidas durante o agendamento público</p>
        </div>
        <button className="btn btn-primary" onClick={openCreate}>+ Novo campo</button>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60 }}><div className="spinner" /></div>
      ) : fields.length === 0 ? (
        <div className="empty-state">
          <div style={{ fontSize: 40, marginBottom: 12 }}>📝</div>
          <h3>Nenhum campo configurado</h3>
          <p>Adicione perguntas que serão exibidas ao cliente durante o agendamento.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {fields.map(f => (
            <div key={f.id} className="card" style={{ padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 16 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, color: 'var(--gray-900)', marginBottom: 2 }}>{f.label}</div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <span className="badge badge-info">{FIELD_TYPES.find(t => t.value === f.type)?.label || f.type}</span>
                  {f.required ? <span className="badge badge-error">Obrigatório</span> : <span className="badge" style={{ background: 'var(--gray-100)', color: 'var(--gray-500)' }}>Opcional</span>}
                  {f.service_id && <span className="badge" style={{ background: 'var(--purple-50)', color: 'var(--purple-600)' }}>
                    {services.find(s => s.id === f.service_id)?.name || `Serviço #${f.service_id}`}
                  </span>}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-secondary" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => openEdit(f)}>Editar</button>
                <button className="btn btn-danger" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => onDelete(f.id)}>Remover</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {modal !== null && (
        <div className="modal-overlay" onClick={() => setModal(null)}>
          <div className="modal" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">{modal === 'create' ? 'Novo campo' : 'Editar campo'}</h3>
              <button className="modal-close" onClick={() => setModal(null)}>✕</button>
            </div>
            <form onSubmit={onSave}>
              <div className="modal-body">
                <div className="form-group"><label className="label">Label / Pergunta *</label>
                  <input className="input" value={form.label} onChange={e => setForm(f => ({ ...f, label: e.target.value }))} required />
                </div>
                <div className="form-group"><label className="label">Tipo</label>
                  <select className="input" value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}>
                    {FIELD_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                {form.type === 'select' && (
                  <div className="form-group"><label className="label">Opções (uma por linha)</label>
                    <textarea className="input" value={form.options} onChange={e => setForm(f => ({ ...f, options: e.target.value }))} rows={4} placeholder="Opção 1&#10;Opção 2&#10;Opção 3" />
                  </div>
                )}
                <div className="form-group"><label className="label">Serviço (deixe em branco para todos)</label>
                  <select className="input" value={form.service_id} onChange={e => setForm(f => ({ ...f, service_id: e.target.value }))}>
                    <option value="">Todos os serviços</option>
                    {services.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
                <div className="form-group"><label className="label">Ordem (menor = primeiro)</label>
                  <input className="input" type="number" value={form.sort_order} onChange={e => setForm(f => ({ ...f, sort_order: parseInt(e.target.value) || 0 }))} />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <input type="checkbox" id="req" checked={form.required} onChange={e => setForm(f => ({ ...f, required: e.target.checked }))} />
                  <label htmlFor="req" style={{ fontSize: 14, cursor: 'pointer' }}>Campo obrigatório</label>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setModal(null)}>Cancelar</button>
                <button type="submit" className={`btn btn-primary${saving ? ' btn-loading' : ''}`} disabled={saving}>{saving ? '' : 'Salvar'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
